<template>
  <v-row justify="center">
    <v-col v-if="!kubraPaymentInProgress" cols="12" class="px-4">
      <!-- Payment Details -->
      <h2 role="heading" aria-level="3" class="text-h3 text-center font-weight-bold mb-4">
        {{ $t('L_policy_payment_details') }}
      </h2>
      <v-row class="text-center" justify="center">
        <!-- Payment Method -->
        <v-col cols="12" class="font-weight-medium mt-3">
          <v-row justify="center">
            <!-- :cols="hasPaymentEditFeature ? 10 : 12" -->
            <v-col class="text-right text-h4" cols="auto">
              {{ $t('L_policy_billing_method') }}
            </v-col>
            <v-col v-if="hasPaymentEditFeature" cols="auto">
              <a
                id="edit_billing_method"
                href="javascript:void(0)"
                class="blueLinkPointer"
                :aria-label="$t('L_payment_edit') + ' payment details'"
                @click="editPaymentMethod"
                >{{ $t('L_payment_edit') }}
              </a>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" justify="center">
          <h3 class="text-h4 font-weight-regular">{{ $t(paymentMethod) }}</h3>
        </v-col>
        <!-- Bank Account Type -->
        <v-col cols="12" class="font-weight-medium mt-3" v-if="currentPolicy.isB2BillingForm">
          <v-row justify="center">
            <!-- :cols="hasBankAccountEditFeature ? 10 : 12" -->
            <v-col class="text-right" cols="auto">
              {{ $t(accountType) }}
            </v-col>
            <v-col v-if="hasBankAccountEditFeature" cols="auto">
              <a
                id="edit_account_type"
                class="blueLinkPointer"
                href="javascript:void(0)"
                @click="startBankChange"
                >{{ $t('L_payment_edit') }}
              </a>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <!-- Bank Account Info -->
      <v-card-text v-if="showBankInfo">
        <v-row justify="center" class="text-center">
          <v-col cols="12" sm="12" md="4">
            <h4 class="text-h4">{{ $t('L_policy_bank_name') }}</h4>
            <h3 class="text-h4 font-weight-regular">{{ bank.name }}</h3>
          </v-col>
          <v-col cols="12" sm="12" md="4">
            <h4 class="text-h4">{{ $t('L_policy_account_number') }}</h4>
            <h3 class="text-h4 font-weight-regular">{{ hideNumbers(bank.account) }}</h3>
          </v-col>
          <v-col cols="12" sm="12" md="4">
            <h4 class="text-h4">{{ $t('L_policy_payor_name') }}</h4>
            <h3 class="text-h4 font-weight-regular">{{ bank.payorName }}</h3>
          </v-col>
        </v-row>
      </v-card-text>
    </v-col>
    <KubraPayment v-if="kubraPaymentInProgress" />
  </v-row>
</template>
<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { filters } from '@/mixins/filters';
import stringValues from '@/shared/util/stringValues';
import PolicyDetailsInfo from '../../PolicyDetailsInfo.vue';
import KubraPayment from './KubraPayment.vue';
import DynatraceClient from '@/plugins/dynatracePlugin';
import { mapActions, mapState, mapStores } from 'pinia';
import { useGlobalStore } from '@/stores/global/global.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { RouteNames } from '@/router/routeNames';

const componentOptions: ComponentOptions = {
  name: 'ExpandedPaymentCard',
  mixins: [filters],
  components: {
    KubraPayment,
    PolicyDetailsInfo,
  },
  computed: {
    ...mapStores(useGlobalStore),
    ...mapState(useGlobalStore, ['containsFeature']),
    ...mapState(usePolicyStore, [
      'currentPolicy',
      'updatingBank',
      'preLoadBank',
      'paymentData',
      'policyData',
    ]),
    bank() {
      return this.currentPolicy.paymentInfo?.bank;
    },
    hasPaymentEditFeature() {
      return (
        this.globalStore.containsFeature('PAY-EDIT') &&
        (!this.currentPolicy.isB2BillingForm || this.currentPolicy.billingMode === '01')
      );
    },
    hasBankAccountEditFeature() {
      return this.globalStore.containsFeature('BANKACCOUNT-EDIT');
    },
    displayDatePicker() {
      return !this.containsFeature('DRAFT-DATE');
    },
    accountType() {
      if (this.currentPolicy?.paymentInfo?.isSavingsAccount) {
        return 'L_payment_accnttype_saving';
      }
      return this.isCanadianPolicy
        ? 'L_payment_accnttype_chequing'
        : 'L_payment_accnttype_checking';
    },
    showBankInfo() {
      return Object.keys(this.bank).length > 0 && this.currentPolicy.isB2BillingForm;
    },
    paymentMethod() {
      return stringValues.paymentMethod(this.currentPolicy);
    },
    isCanadianPolicy() {
      return !!this.currentPolicy?.isCanadaPolicy;
    },
    kubraPaymentInProgress() {
      return (
        this.currentPolicy?.kubraPaymentStatus !== null &&
        this.currentPolicy?.kubraPaymentStatus !== undefined
      );
    },
  },
  methods: {
    ...mapActions(usePolicyStore, {
      setDisablePaymentDetailsLink: 'setDisablePaymentDetailsLink',
      setPanelValue: 'setPanelValue',
      setChangeBankAccountStep: 'setChangeBankAccountStep',
      setPayMethodChange: 'setPayMethodChange',
    }),
    async editPaymentMethod() {
      try {
        const i = DynatraceClient.enterAction('PolicyDetails_BillingMethod_Edit_selected');
        DynatraceClient.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
      if (this.currentPolicy.isB2BillingForm || this.currentPolicy.isA1BillingForm) {
        await this.globalStore.postAuditLog({
          actionId: 'UpdatePaymentMethod_StartAction',
          details: `Policy Number: ${this.policyData?.contractId}`,
        });
        this.setPayMethodChange(1);
        this.$router.push({
          name: RouteNames.PAYMETHODCHANGE,
          params: { contractId: this.indexPolicy },
        });
        // this.$emit('editPayMethod');
      }
    },
    startBankChange() {
      if (this.currentPolicy.isB2BillingForm) {
        const paymentData = {
          accountNumber: '',
          bankName: '',
          routingNumber: '',
        };
        this.paymentData = paymentData;
        this.setChangeBankAccountStep(1);
        this.$router.push({
          name: RouteNames.CHANGEBANKACCOUNTSTEP,
          params: { contractId: this.indexPolicy },
        });
        // this.preLoadBank = true;
        // this.setDisablePaymentDetailsLink(true);
        // this.updatingBank = true;
      }
    },
    cancel() {
      this.setDisablePaymentDetailsLink(false);
      this.updatingBank = false;
      this.setPanelValue(-1);
    },
    cancelBankUpdate() {
      this.setDisablePaymentDetailsLink(false);
      this.updatingBank = false;
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss">
.blueLinkPointer {
  text-decoration: none;
  color: rgb(12, 109, 202);
  font-weight: 600;
  cursor: pointer;
}
</style>
